import { useState } from 'react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import { deactivateDepartmentsLisense } from '../../services/apiService';
import { useToast } from '../../hooks/use-toast';
import type { Facility } from './utils/utils';

interface DeactivateFacilityDialogProps {
    facility: Facility | null;
    onOpenChange: (open: boolean) => void;
    onDeactivated: () => void | Promise<void>;
}

export default function DeactivateFacilityDialog({
    facility,
    onOpenChange,
    onDeactivated,
}: DeactivateFacilityDialogProps) {
    const { toast } = useToast();
    const [deactivating, setDeactivating] = useState(false);

    const handleConfirm = async () => {
        if (!facility) return;
        setDeactivating(true);
        try {
            const success = await deactivateDepartmentsLisense(facility._id);
            if (!success) throw new Error('Failed to deactivate facility');
            toast({ title: 'Facility deactivated' });
            onOpenChange(false);
            await onDeactivated();
        } catch {
            toast({
                title: 'Could not deactivate facility',
                variant: 'destructive',
            });
        } finally {
            setDeactivating(false);
        }
    };

    return (
        <AlertDialog open={Boolean(facility)} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Deactivate facility?</AlertDialogTitle>
                    <AlertDialogDescription>
                        {facility
                            ? `This will deactivate ${facility.name}. Users will lose access.`
                            : 'This will deactivate the facility.'}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={deactivating}>
                        Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                        disabled={deactivating}
                        onClick={handleConfirm}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                        {deactivating ? 'Deactivating...' : 'Deactivate'}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
